import { z } from 'zod';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';

/**
 * Clients surface (T-0021 / E01-F01). The list view powers the dashboard
 * — every Project (engagement) lives under a Client, so the dashboard
 * stitches the two together client-side for v1.
 *
 * T-0108 extends the surface with `useClientsDetailedQuery` — a
 * useQueries-based hook that fans out one `GET /api/v1/clients/:id`
 * request per row of the firm-wide list so the `/clients` table can
 * render team rosters without a dedicated rollup endpoint. The fan-out
 * is fine for the expected firm size (low tens of clients); if firms
 * scale past that, an `?include=team_members` projection on the list
 * endpoint is the natural follow-up.
 */

export const ClientSummarySchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  client_type: z.string(),
  status: z.string(),
});
export type ClientSummary = z.infer<typeof ClientSummarySchema>;

export const ClientDetailSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  client_type: z.string(),
  primary_contact_name: z.string(),
  primary_contact_email: z.string(),
  /** T-0227: free-text notes from the client page's Edit flow; null when none. */
  notes: z.string().nullable().optional(),
  status: z.string(),
  team_member_user_ids: z.array(z.string()),
  created_at: z.string(),
  updated_at: z.string(),
});
export type ClientDetail = z.infer<typeof ClientDetailSchema>;

export interface ListClientsOptions {
  /** Name search, case-insensitive substring (T-0227 command search). */
  q?: string;
}

export function listClients(opts: ListClientsOptions = {}): Promise<ClientSummary[]> {
  return apiClient('/api/v1/clients', z.array(ClientSummarySchema), {
    query: { q: opts.q },
  });
}

export function getClient(id: string): Promise<ClientDetail> {
  return apiClient(`/api/v1/clients/${encodeURIComponent(id)}`, ClientDetailSchema);
}

export function useClientsQuery() {
  return useQuery({
    queryKey: ['clients', 'list'] as const,
    queryFn: () => listClients(),
  });
}

export function useClientQuery(id: string | undefined) {
  return useQuery({
    queryKey: ['clients', 'detail', id] as const,
    queryFn: () => getClient(id!),
    enabled: Boolean(id),
  });
}

/**
 * Create-client payload (T-0117 wizard, step 1 "+ Create new client" branch).
 * Mirrors `apps/api/src/services/clients.ts → ClientCreateInput`. The backend
 * validates `client_type` against the enum
 * (`state_agency | county | city | other`); the wizard surfaces those four
 * options verbatim.
 */
export interface CreateClientInput {
  name: string;
  client_type: string;
  primary_contact_name: string;
  primary_contact_email: string;
  /** Defaults to `[]` if omitted — wizard doesn't ask for team members. */
  team_member_user_ids?: string[];
}

export const CreatedClientSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  status: z.string(),
});
export type CreatedClient = z.infer<typeof CreatedClientSchema>;

/**
 * POST /api/v1/clients — wires the wizard's "+ Create new client" branch.
 * The route accepts `Idempotency-Key`; the wizard passes a fresh UUID so a
 * double-click on Continue doesn't double-create. On success the cached
 * client list is invalidated so the dashboard refreshes immediately.
 */
export function createClient(
  input: CreateClientInput,
  idempotencyKey?: string,
): Promise<CreatedClient> {
  return apiClient('/api/v1/clients', CreatedClientSchema, {
    method: 'POST',
    body: {
      name: input.name,
      client_type: input.client_type,
      primary_contact_name: input.primary_contact_name,
      primary_contact_email: input.primary_contact_email,
      team_member_user_ids: input.team_member_user_ids ?? [],
    },
    ...(idempotencyKey ? { idempotencyKey } : {}),
  });
}

export function useCreateClientMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      input,
      idempotencyKey,
    }: {
      input: CreateClientInput;
      idempotencyKey?: string;
    }) => createClient(input, idempotencyKey),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['clients', 'list'] });
    },
  });
}

/**
 * PATCH /api/v1/clients/:id — the client page's Edit flow and team
 * assignment (T-0227 / C2-10). Mirrors `ClientPatchInput` in
 * `apps/api/src/services/clients.ts`. Firm admins and engagement leads on
 * the client's team; the server decides and the page only hides the
 * controls. Last-write-wins: no idempotency key.
 */
export interface UpdateClientInput {
  name?: string;
  primary_contact_name?: string;
  primary_contact_email?: string;
  /** `null` clears the notes. */
  notes?: string | null;
  add_team_member_user_ids?: string[];
  remove_team_member_user_ids?: string[];
}

export const UpdatedClientSchema = z.object({
  id: z.string().uuid(),
  updated_at: z.string(),
});
export type UpdatedClient = z.infer<typeof UpdatedClientSchema>;

export function updateClient(id: string, input: UpdateClientInput): Promise<UpdatedClient> {
  return apiClient(`/api/v1/clients/${encodeURIComponent(id)}`, UpdatedClientSchema, {
    method: 'PATCH',
    body: input,
  });
}

export function useUpdateClientMutation(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateClientInput) => updateClient(id, input),
    onSuccess: () => {
      // The detail page re-reads itself; the list and the engagement
      // breadcrumbs carry the client name, so they refresh too.
      void qc.invalidateQueries({ queryKey: ['clients'] });
      void qc.invalidateQueries({ queryKey: ['engagements'] });
    },
  });
}

/**
 * Fan out one detail query per client id. Returns the full
 * `useQueries` result array so the caller can surface partial loading +
 * partial error states (one slow client shouldn't block the table).
 *
 * Query keys share the `['clients', 'detail', id]` prefix with
 * `useClientQuery` so React-Query de-duplicates across the table view
 * and any side-panel that also fetches a single client.
 */
export function useClientsDetailedQuery(ids: ReadonlyArray<string>) {
  return useQueries({
    queries: ids.map((id) => ({
      queryKey: ['clients', 'detail', id] as const,
      queryFn: () => getClient(id),
      enabled: Boolean(id),
      staleTime: 30_000,
    })),
  });
}
